import React, { useContext, useRef, useState } from "react";

import { SATOSContentRender } from "../Application/SATOSContentRender";
import { SATOSLink } from "../Application/SATOSLink";
import {
	AttachFileOutlined as AttachFileOutlinedIcon,
	ChatBubbleOutline as ChatBubbleOutlineIcon,
	CheckCircle as CheckCircleIcon,
	Clear as ClearIcon,
	Close as CloseIcon,
	Comment as CommentIcon,
	ContactsOutlined,
	Done as DoneIcon,
	Error as ErrorIcon,
	ExpandLess as ExpandLessIcon,
	ExpandMore as ExpandMoreIcon,
	HighlightOffOutlined as HighlightOffOutlinedIcon,
	HourglassEmpty as HourglassEmptyIcon,
	LaunchOutlined as LaunchOutlinedIcon,
	MoreVert as MoreVertIcon,
	Save as SaveIcon,
} from "@mui/icons-material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
	Alert,
	Avatar,
	Box,
	Button,
	Card,
	CardContent,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Divider,
	Grid,
	IconButton,
	Link,
	Menu,
	MenuItem,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import Fade from "@mui/material/Fade";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import type { MenuProps } from "@mui/material/Menu";
import { alpha, styled } from "@mui/material/styles";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import rehypeRaw from "rehype-raw";

import { useTime } from "@/components/Application/TimeContext";
import { Translate } from "@/components/Application/Translate";
import { stringAvatar } from "@/components/Procedures/Checklist/utils/Helpers";

import { AlertConfirmDeleteComment } from "@/helpers/alerts/AlertConfirm";
import { AlertToast } from "@/helpers/alerts/AlertToast";

import { UserContext } from "@/model/User/UserContext";

import { UseAddProcedureUpdateSuggestionComment } from "@/service/ProcedureMutations";

dayjs.extend(utc);

type Suggestion = any;

interface Props {
	suggestion: Suggestion;
	selectedChecklistName: any;
	selectedCard: boolean;
	page: string;
	selectedChecklistProcedureId: any;
	onOpenSuggestion?: (suggestion: any) => void;
	handleStepIdSelected: (stepId: number) => void;
	handleUpdateStatusForCard: (
		suggestionId: number,
		status: string,
		extra: {
			procedureId: number;
			stepId: number;
			description: string;
			anonymous: boolean;
			attachment?: any;
			declinedComment?: string;
		},
	) => void;
	handleDeleteComment: (commentId: number) => void;
	refreshSuggestions: () => void;
}

const isTypingTarget = (el: EventTarget | null) => {
	if (!el || !(el as HTMLElement).tagName) return false;
	const node = el as HTMLElement;
	const tag = node.tagName.toLowerCase();
	const editable = node.getAttribute?.("contenteditable") === "true";
	return editable || tag === "input" || tag === "textarea" || tag === "select";
};

const statusVisual = (status?: string) => {
	const s = String(status || "").toLowerCase();
	if (s === "verified")
		return {
			color: "success" as const,
			icon: <CheckCircleIcon fontSize="small" />,
		};
	if (s === "declined")
		return { color: "error" as const, icon: <ErrorIcon fontSize="small" /> };
	if (s === "closed")
		return {
			color: "default" as const,
			icon: <HighlightOffOutlinedIcon fontSize="small" />,
		};
	return {
		color: "warning" as const,
		icon: <HourglassEmptyIcon fontSize="small" />,
	}; // Unverified / other
};

const CustomCard: React.FC<Props> = ({
	handleDeleteComment,
	selectedChecklistProcedureId,
	selectedChecklistName,
	suggestion,
	selectedCard,
	handleUpdateStatusForCard,
	handleStepIdSelected,
	page,
	refreshSuggestions,
}) => {
	const user = useContext(UserContext);
	const { timezone } = useTime();

	const [isDeclineModalOpen, setDeclineModalOpen] = useState(false);
	const [isCommentModalOpen, setCommentModalOpen] = useState(false);
	const [declineComment, setDeclineComment] = useState("");
	const [comment, setComment] = useState("");
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const [expanded, setExpanded] = useState(false);

	const menuBtnRef = useRef<HTMLButtonElement | null>(null);

	const isSelected = selectedCard;
	const AddComment = UseAddProcedureUpdateSuggestionComment();

	const onCardClick = () => {
		handleStepIdSelected(suggestion.StepId);
		onOpenSuggestion?.(suggestion);
	};
	const changeStatus = (status: string, cmt = "") => {
		handleUpdateStatusForCard(suggestion.Id, status, {
			procedureId: suggestion.ProcedureId,
			stepId: suggestion.StepId,
			description: suggestion.Description,
			anonymous: suggestion.Anonymous,
			attachment: suggestion.Attachment,
			declinedComment: cmt,
		});
	};

	const submitComment = async () => {
		if (!comment.trim()) return;
		try {
			await AddComment({
				suggestionId: suggestion.Id,
				description: comment.trim(),
				createdById: user?.Id,
				createdByName: user?.Name,
				created: new Date().toISOString(),
			});
			AlertToast.success("Comment added.");
			setComment("");
			setCommentModalOpen(false);
			refreshSuggestions();
		} catch {
			AlertToast.failed("Failed to add comment.");
		}
	};

	type ActionItem = {
		label: string;
		icon: React.ReactNode;
		action: () => void;
		tone?: Tone;
		desc?: string;
		kbd?: string; // keyboard hint
		dividerAbove?: boolean;
	};

	const menuItems = (): ActionItem[] => {
		const items: ActionItem[] = [];

		if (page !== "verified") {
			items.push({
				label: "Verify",
				icon: <DoneIcon fontSize="small" />,
				action: () => changeStatus("Verified"),
				tone: "success",
				desc: "Mark this suggestion as verified",
				kbd: "V",
			});
		}

		if (page === "declined") {
			items.push(
				{
					dividerAbove: true,
					label: "Move to Unverified",
					icon: <HourglassEmptyIcon fontSize="small" />,
					action: () => changeStatus("Unverified"),
					tone: "warning",
					desc: "Reopen and set back to unverified",
					kbd: "U",
				},
				{
					label: "Add Comment",
					icon: <CommentIcon fontSize="small" />,
					action: () => setCommentModalOpen(true),
					tone: "info",
					desc: "Discuss or leave a note",
					kbd: "C",
				},
				{
					label: "Close",
					icon: <HighlightOffOutlinedIcon fontSize="small" />,
					action: () =>
						changeStatus("Closed", suggestion.DeclinedComment || ""),
					tone: "default",
					desc: "Resolve without further action",
					kbd: "X",
				},
			);
		} else {
			items.push(
				{
					dividerAbove: true,
					label: "Decline",
					icon: <ClearIcon fontSize="small" />,
					action: () => changeStatus("Declined"),
					tone: "error",
					desc: "Reject without comment",
					kbd: "D",
				},
				{
					label: "Decline with comment",
					icon: <ChatBubbleOutlineIcon fontSize="small" />,
					action: () => setDeclineModalOpen(true),
					tone: "warning",
					desc: "Reject and provide a reason",
				},
				{
					label: "Comment",
					icon: <CommentIcon fontSize="small" />,
					action: () => setCommentModalOpen(true),
					tone: "info",
					desc: "Add a discussion comment",
					kbd: "C",
				},
			);
		}

		if (page === "verified") {
			items.push({
				dividerAbove: true,
				label: "Close",
				icon: <HighlightOffOutlinedIcon fontSize="small" />,
				action: () => changeStatus("Closed"),
				tone: "default",
				desc: "Resolve and archive",
				kbd: "X",
			});
		}

		return items;
	};

	const { color: statusColor, icon: statusIcon } = statusVisual(
		suggestion.Status,
	);
	const createdLocal = dayjs
		.utc(suggestion.Created)
		.tz(timezone)
		.format("DD/MM/YY HH:mm");

	return (
		<>
			<Card
				onClick={onCardClick}
				tabIndex={0}
				role="group"
				aria-label={`Suggestion for step ${suggestion.StepId}`}
				onKeyDown={(e) => {
					if (isTypingTarget(e.target)) return;

					const key = e.key.toLowerCase();

					if (key === "enter" || key === " ") {
						e.preventDefault();
						onCardClick();
						return;
					}

					if (key === "m") {
						e.preventDefault();
						if (menuBtnRef.current) {
							setAnchorEl(menuBtnRef.current);
							setTimeout(() => {
								const first = document.querySelector(
									'[role="menu"] [role="menuitem"]',
								) as HTMLElement | null;
								first?.focus();
							}, 0);
						}
						return;
					}

					const map: Record<string, string> = {
						v: "V",
						u: "U",
						d: "D",
						c: "C",
						x: "X",
					};
					if (map[key]) {
						e.preventDefault();
						const items = menuItems();
						const item = items.find(
							(it) => (it.kbd || "").toLowerCase() === key,
						);
						if (item) item.action();
					}

					if (key === "escape" && anchorEl) {
						e.preventDefault();
						setAnchorEl(null);
						(e.currentTarget as HTMLElement).focus();
					}
				}}
				sx={(t) => ({
					mt: 1,
					pb: 2,
					borderRadius: 2,
					position: "relative",
					overflow: "hidden",
					cursor: "pointer",
					border: `1px solid ${t.palette.divider}`,
					boxShadow: isSelected
						? "0 6px 20px rgba(0,0,0,.10)"
						: "0 1px 2px rgba(0,0,0,.04)",
					transition:
						"transform .15s ease, box-shadow .15s ease, border-color .15s ease",
					transform: isSelected ? "translateY(-1px)" : "none",
					"&:hover": { boxShadow: "0 6px 18px rgba(0,0,0,.08)" },
					"&:focus-visible": {
						outline: `3px solid ${alpha(t.palette.primary.main, 0.35)}`,
						outlineOffset: 2,
					},
					"&:before": {
						content: '""',
						position: "absolute",
						top: 0,
						bottom: 0,
						left: 0,
						width: 4,
						background:
							statusColor === "success"
								? t.palette.success.main
								: statusColor === "error"
									? t.palette.error.main
									: statusColor === "warning"
										? t.palette.warning.main
										: t.palette.divider,
						opacity: 0.9,
					},
					backgroundColor: isSelected
						? alpha(t.palette.primary.main, 0.04)
						: "background.paper",
				})}
			>
				{/* HEADER */}
				<Box
					sx={{
						px: 2,
						pt: 1.25,
						pb: 0.75,
						display: "flex",
						alignItems: "center",
						gap: 1,
					}}
				>
					{/* Procedure link (black) */}
					{!!selectedChecklistProcedureId && (
						<Box sx={{ flex: 1, minWidth: 0, px: 1 }}>
							<Tooltip
								title={`Go to procedure${suggestion.ProcedureName ? `: ${suggestion.ProcedureName}` : ""}`}
								placement="top"
								arrow
								enterDelay={400}
							>
								<SATOSLink
									to="AccessProcedure"
									target="_blank"
									params={[selectedChecklistProcedureId]}
									className="accordion-item-link"
									onClick={(e: React.MouseEvent) => e.stopPropagation()}
									style={{
										display: "grid",
										gridTemplateColumns: "1fr auto", // text | arrow
										alignItems: "center",
										columnGap: 8,
										textDecoration: "none",
										color: "inherit",
										minWidth: 0,
										width: "100%",
									}}
									aria-label={`Open procedure ${selectedChecklistName}`}
								>
									<Typography
										variant="subtitle2"
										fontWeight={800}
										title={
											suggestion.ProcedureName ??
											`Procedure ${selectedChecklistName}`
										}
										sx={{
											minWidth: 0,
											overflow: "hidden",
											whiteSpace: "nowrap",
											textOverflow: "ellipsis",
											color: "text.primary",
										}}
									>
										{suggestion.ProcedureName ??
											`Procedure ${selectedChecklistName}`}
									</Typography>

									<OpenInNewIcon fontSize="inherit" />
								</SATOSLink>
							</Tooltip>
						</Box>
					)}

					{/* Status chip */}
					<Chip
						size="small"
						color={statusColor}
						variant="filled"
						icon={statusIcon}
						label={suggestion.Status}
						sx={{ mr: 0.5 }}
					/>

					{/* Menu trigger */}
					<Tooltip title="More actions (M)">
						<IconButton
							size="small"
							ref={menuBtnRef}
							aria-haspopup="menu"
							aria-controls={anchorEl ? "suggestion-actions-menu" : undefined}
							aria-expanded={Boolean(anchorEl)}
							aria-keyshortcuts="M"
							onClick={(e) => {
								e.stopPropagation();
								setAnchorEl(e.currentTarget);
							}}
							sx={{ ml: "auto" }}
						>
							<MoreVertIcon fontSize="small" />
						</IconButton>
					</Tooltip>

					<StyledMenu
						id="suggestion-actions-menu"
						anchorEl={anchorEl}
						open={Boolean(anchorEl)}
						onClose={() => setAnchorEl(null)}
						onClick={(e) => e.stopPropagation()}
						MenuListProps={{
							dense: true,
							autoFocus: true,
							onKeyDown: (e: React.KeyboardEvent) => {
								if (e.key.toLowerCase() === "escape") {
									e.preventDefault();
									setAnchorEl(null);
									menuBtnRef.current?.focus();
								}
							},
						}}
					>
						{menuItems().map(
							(
								{
									label,
									icon,
									action,
									tone = "default",
									desc,
									kbd,
									dividerAbove,
								},
								idx,
							) => (
								<React.Fragment key={idx}>
									{dividerAbove && <Divider sx={{ my: 0.5 }} />}
									<MenuItem
										role="menuitem"
										aria-keyshortcuts={kbd}
										onClick={() => {
											action();
											setAnchorEl(null);
											menuBtnRef.current?.focus();
										}}
										sx={(t) => ({
											alignItems: "center",
											"&:hover .MuiListItemIcon-root": {
												color: toneColor(t, tone),
											},
										})}
									>
										<ListItemIcon
											sx={(t) => ({ minWidth: 30, color: toneColor(t, tone) })}
										>
											{icon}
										</ListItemIcon>
										<ListItemText
											primary={
												<Typography variant="body2" sx={{ fontWeight: 600 }}>
													{label}
												</Typography>
											}
											secondary={
												desc ? (
													<Typography variant="caption" color="text.secondary">
														{desc}
													</Typography>
												) : undefined
											}
										/>
										{kbd && <Kbd>{kbd}</Kbd>}
									</MenuItem>
								</React.Fragment>
							),
						)}
					</StyledMenu>
				</Box>

				<Divider />

				{/* BODY */}
				<CardContent sx={{ pt: 1.25, pb: 1.25 }}>
					<Grid container spacing={1.25} alignItems="flex-start">
						{/* description */}

						<Grid item xs={12}>
							<Typography
								variant="body1"
								sx={{
									fontWeight: 600,
									lineHeight: 1.35,
									mb: 0.25,
									wordBreak: "break-word",
								}}
							>
								<Translate String="Tasks.Procedure.Card.Description">
									Description
								</Translate>
							</Typography>
							<Typography
								variant="body2"
								color="text.secondary"
								sx={{
									display: "-webkit-box",
									WebkitLineClamp: 4,
									WebkitBoxOrient: "vertical",
									overflow: "hidden",
									whiteSpace: "normal",
								}}
							>
								{suggestion.Description || "—"}
							</Typography>
						</Grid>

						{/* meta row */}
						<Grid item xs={12}>
							<Box
								sx={{
									display: "flex",
									alignItems: "center",
									flexWrap: "wrap",
									gap: 0.75,
								}}
							>
								<Chip
									size="small"
									variant="outlined"
									label={
										<Box
											sx={{
												display: "inline-flex",
												alignItems: "center",
												gap: 0.75,
											}}
										>
											<Avatar
												{...stringAvatar(suggestion.CreatedBy?.Name || "U")}
												sx={{ width: 18, height: 18, fontSize: 10 }}
											/>
											<Typography variant="caption" sx={{ fontWeight: 600 }}>
												{suggestion.CreatedBy?.Name || "Unknown"}
											</Typography>
										</Box>
									}
								/>
								<Chip
									size="small"
									variant="outlined"
									label={
										<Box
											sx={{
												display: "inline-flex",
												alignItems: "center",
												gap: 0.5,
											}}
										>
											<Typography variant="caption" color="text.secondary">
												<Translate String="Tasks.Procedure.Card.Created">
													Created
												</Translate>
												:
											</Typography>
											<Typography variant="caption">{createdLocal}</Typography>
										</Box>
									}
								/>

								{!!suggestion.Attachment && (
									<Chip
										size="small"
										variant="outlined"
										icon={<AttachFileOutlinedIcon fontSize="small" />}
										label={
											<Box
												sx={{
													display: "inline-flex",
													alignItems: "center",
													gap: 0.5,
													maxWidth: 220,
												}}
											>
												<Typography
													variant="caption"
													noWrap
													title={suggestion.Attachment.FileName}
													sx={{ maxWidth: 180 }}
												>
													{suggestion.Attachment.FileName}
												</Typography>
												<LaunchOutlinedIcon
													sx={{ width: 14, height: 14, opacity: 0.7 }}
												/>
											</Box>
										}
										onClick={(e) => {
											e.stopPropagation();
											window.open(suggestion.Attachment.Url, "_blank");
										}}
									/>
								)}
							</Box>
						</Grid>

						{/* comments toggle */}
						{Array.isArray(suggestion.Comments) &&
							suggestion.Comments.length > 0 && (
								<Grid item xs={12}>
									<Button
										size="small"
										onClick={(e) => {
											e.stopPropagation();
											setExpanded((v) => !v);
										}}
										sx={{
											borderRadius: 2,
											px: 1,
											gap: 0.5,
											textTransform: "none",
											"&:hover": {
												backgroundColor: (t) =>
													alpha(t.palette.primary.main, 0.06),
											},
										}}
									>
										{expanded ? "Hide comments" : "Show comments"}
										{expanded ? (
											<ExpandLessIcon fontSize="small" />
										) : (
											<ExpandMoreIcon fontSize="small" />
										)}
										<Chip
											size="small"
											label={suggestion.Comments.length}
											sx={{
												ml: 0.5,
												height: 20,
												"& .MuiChip-label": {
													px: 0.75,
													fontSize: 12,
													fontWeight: 700,
												},
											}}
										/>
									</Button>
								</Grid>
							)}
					</Grid>

					{/* comments list */}
					{expanded && Array.isArray(suggestion.Comments) && (
						<Box sx={{ mt: 1.25, pl: 0.25 }}>
							{suggestion.Comments.slice()
								.sort(
									(a: any, b: any) =>
										new Date(b.Created).valueOf() -
										new Date(a.Created).valueOf(),
								)
								.map((c: any) => {
									const created = dayjs
										.utc(c.Created)
										.tz(timezone)
										.format("DD/MM/YY HH:mm:ss");
									return (
										<Box
											key={c.Id}
											sx={(t) => ({
												display: "grid",
												gridTemplateColumns: "28px 1fr auto",
												alignItems: "start",
												gap: 1,
												py: 1,
												borderBottom: `1px dashed ${t.palette.divider}`,
											})}
											onClick={(e) => e.stopPropagation()}
										>
											<Tooltip title={c.CreatedByName || ""}>
												<Avatar
													{...stringAvatar(c.CreatedByName || "U")}
													sx={{ width: 28, height: 28, fontSize: 12 }}
												/>
											</Tooltip>

											<Box sx={{ minWidth: 0 }}>
												<Box sx={{ mb: 0.25 }}>
													<SATOSContentRender
														rehypePlugins={[rehypeRaw]}
														content={c.Description}
													/>
												</Box>
												<Typography variant="caption" color="text.secondary">
													{created}
												</Typography>
											</Box>

											<Tooltip title="Delete comment">
												<IconButton
													size="small"
													onClick={() =>
														AlertConfirmDeleteComment(
															() => handleDeleteComment(c.Id),
															c.Id,
														)
													}
													sx={{ ml: 0.5 }}
												>
													<ClearIcon fontSize="small" />
												</IconButton>
											</Tooltip>
										</Box>
									);
								})}
						</Box>
					)}
				</CardContent>

				{/* declined note */}
				{!!suggestion.DeclinedComment && (
					<>
						<Divider />
						<Box
							sx={(t) => ({
								px: 2,
								py: 1.25,
								bgcolor: alpha(t.palette.error.main, 0.06),
								borderTop: `1px solid ${alpha(t.palette.error.main, 0.25)}`,
							})}
							onClick={(e) => e.stopPropagation()}
						>
							<Alert
								icon={<ErrorIcon fontSize="small" />}
								severity="warning"
								variant="outlined"
								sx={{
									borderRadius: 1.5,
									px: 1.25,
									py: 1,
									borderColor: (t) => alpha(t.palette.error.main, 0.35),
								}}
							>
								<Typography variant="body2" sx={{ fontWeight: 700, mb: 0.25 }}>
									Comment
								</Typography>
								<Typography variant="body2">
									{suggestion.DeclinedComment}
								</Typography>
							</Alert>
						</Box>
					</>
				)}
			</Card>

			{/* Decline dialog */}
			<Dialog
				open={isDeclineModalOpen}
				onClose={() => setDeclineModalOpen(false)}
				fullWidth
				maxWidth="xs"
				PaperProps={{ sx: { borderRadius: 2, p: 1 } }}
				onClick={(e) => e.stopPropagation()}
			>
				<DialogTitle
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						py: 1,
						px: 2,
					}}
				>
					<Translate String="MaterialHandling.DeclineSuggestion">
						Decline Suggestion
					</Translate>
					<IconButton size="small" onClick={() => setDeclineModalOpen(false)}>
						<CloseIcon fontSize="small" />
					</IconButton>
				</DialogTitle>
				<DialogContent sx={{ pt: 0.5, pb: 1.5, px: 2 }}>
					<DialogContentText sx={{ mb: 1, fontSize: "0.875rem" }}>
						<Translate String="MaterialHandling.DeclineSuggestionParagraph">
							Please enter a comment for declining the suggestion.
						</Translate>
					</DialogContentText>
					<TextField
						autoFocus
						fullWidth
						multiline
						minRows={3}
						size="small"
						variant="outlined"
						value={declineComment}
						onChange={(e) => setDeclineComment(e.target.value)}
						onKeyDown={(e) => {
							if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "enter") {
								e.preventDefault();
								changeStatus("Declined", declineComment);
								setDeclineModalOpen(false);
							}
						}}
						label={
							<Translate String="MaterialHandling.DeclineComment">
								Decline Comment
							</Translate>
						}
					/>
				</DialogContent>
				<DialogActions sx={{ px: 2, py: 1 }}>
					<Button
						sx={{ borderRadius: 2, textTransform: "none", fontWeight: 900 }}
						onClick={() => setDeclineModalOpen(false)}
						variant="text"
						color="error"
						size="small"
						startIcon={<CloseIcon fontSize="small" />}
					>
						<Translate String="MaterialHandling.Cancel">Cancel</Translate>
					</Button>
					<Button
						sx={{ borderRadius: 2, textTransform: "none", fontWeight: 900 }}
						onClick={() => {
							changeStatus("Declined", declineComment);
							setDeclineModalOpen(false);
						}}
						variant="contained"
						color="success"
						size="small"
						startIcon={<SaveIcon fontSize="small" />}
					>
						<Translate String="MaterialHandling.Submit">Submit</Translate>
					</Button>
				</DialogActions>
			</Dialog>

			{/* Comment dialog */}
			<Dialog
				open={isCommentModalOpen}
				onClose={() => setCommentModalOpen(false)}
				fullWidth
				maxWidth="xs"
				PaperProps={{ sx: { borderRadius: 2, p: 1 } }}
				onClick={(e) => e.stopPropagation()}
			>
				<DialogTitle
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						py: 1,
						px: 2,
					}}
				>
					<Translate String="ProcedureUpdates.AddCommentSuggestion">
						Add comment
					</Translate>
					<IconButton size="small" onClick={() => setCommentModalOpen(false)}>
						<CloseIcon fontSize="small" />
					</IconButton>
				</DialogTitle>
				<DialogContent sx={{ pt: 0.5, pb: 1.5, px: 2 }}>
					<DialogContentText sx={{ mb: 1, fontSize: "0.875rem" }}>
						<Translate String="ProcedureUpdates.AddCommentSuggestion.Caption">
							Please enter your comment below
						</Translate>
					</DialogContentText>
					<TextField
						autoFocus
						fullWidth
						multiline
						minRows={3}
						size="small"
						variant="outlined"
						value={comment}
						onChange={(e) => setComment(e.target.value)}
						onKeyDown={(e) => {
							if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "enter") {
								e.preventDefault();
								submitComment();
							}
						}}
						label={
							<Translate String="ProcedureUpdates.AddCommentSuggestion.TextField">
								Your comment
							</Translate>
						}
					/>
				</DialogContent>
				<DialogActions sx={{ px: 2, py: 1 }}>
					<Button
						sx={{ borderRadius: 2, textTransform: "none", fontWeight: 900 }}
						onClick={() => setCommentModalOpen(false)}
						variant="text"
						color="error"
						size="small"
						startIcon={<CloseIcon fontSize="small" />}
					>
						<Translate String="MaterialHandling.Cancel">Cancel</Translate>
					</Button>
					<Button
						sx={{ borderRadius: 2, textTransform: "none", fontWeight: 900 }}
						onClick={submitComment}
						variant="contained"
						color="success"
						size="small"
						startIcon={<SaveIcon fontSize="small" />}
					>
						<Translate String="Procedure.Button.Save">Save</Translate>
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};

export default CustomCard;

const StyledMenu = styled((props: MenuProps) => (
	<Menu
		elevation={0}
		TransitionComponent={Fade}
		anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
		transformOrigin={{ vertical: "top", horizontal: "right" }}
		{...props}
	/>
))(({ theme }) => ({
	"& .MuiPaper-root": {
		borderRadius: 12,
		minWidth: 240,
		border: `1px solid ${alpha(theme.palette.divider, 0.7)}`,
		boxShadow: "0 8px 30px rgba(0,0,0,0.08), 0 1px 0 rgba(0,0,0,0.04)",
		backdropFilter: "blur(8px)",
	},
	"& .MuiMenu-list": { padding: 6 },
	"& .MuiMenuItem-root": {
		borderRadius: 8,
		gap: 10,
		paddingTop: 8,
		paddingBottom: 8,
		"&:active": { backgroundColor: alpha(theme.palette.primary.main, 0.08) },
	},
}));

const Kbd: React.FC<React.PropsWithChildren> = ({ children }) => (
	<Box
		component="kbd"
		sx={(t) => ({
			ml: 1,
			px: 0.75,
			py: 0.25,
			borderRadius: 1,
			fontSize: 11,
			lineHeight: 1,
			fontFamily:
				"ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
			border: `1px solid ${alpha(t.palette.text.primary, 0.2)}`,
			color: t.palette.text.secondary,
			background: alpha(t.palette.text.primary, 0.06),
		})}
	>
		{children}
	</Box>
);

type Tone = "default" | "success" | "warning" | "error" | "info";
const toneColor = (t: any, tone: Tone) =>
	tone === "success"
		? t.palette.success.main
		: tone === "warning"
			? t.palette.warning.main
			: tone === "error"
				? t.palette.error.main
				: tone === "info"
					? t.palette.info.main
					: t.palette.text.secondary;
